// Connecting an AI to Tripmo, step by step, for every AI we know: the one source both the app's Connect your AI
// screen (web/src/Connect.tsx) and the public pages at tripmo.me/connect (landing/scripts/connect.ts) read, so the
// steps can't drift apart (features/directories.md, "Connect your AI, on tripmo.me"). Plain data, no imports: the
// app bundles it with Vite, the landing's page builder runs it with Bun.
//
// A step's words are plain text with three marks, which each side renders its own way: **bold** (a button or menu
// as the AI's screen names it), [words](https://…) (a link to that AI's own page, opened in a new tab) and `code`.
// The app's sign-in step names the person ({you}, their email); the public page can't, and says it its own way.
//
// When an AI's screens change, change its steps here, its `checked` day and its `sources`, then run
// `bun run pages` in landing/ and commit what it writes: the landing's check fails until the pages say the same.

// Where every AI connects. The app shows its own (`me.mcp_url`, which is plan-dev's on plan-dev); the public
// pages, this.
export const MCP_URL = "https://app.tripmo.me/mcp";

export interface Step {
  // The words, with the marks above.
  text: string;
  // Under the step, quieter: what only works on a computer, and what to do from a phone.
  note?: string;
  // The address to paste goes under this step.
  address?: boolean;
  // A line to copy under this step, set as code (a command, a config).
  copy?: string;
}

// One way to connect an AI: its website's settings, its command line, a chat it has.
export interface Way {
  // Its heading on the AI's page ("On grok.com"); the first way is the AI's main one, the app's steps.
  title: string;
  steps: Step[];
  // Worth knowing before starting: plans, and workspaces where an admin adds it.
  note?: string;
}

export interface Ai {
  // Its page: tripmo.me/connect/<slug>.
  slug: string;
  name: string;
  // Where it runs, in a few words, for the list of AIs ("claude.ai and the Claude apps").
  where: string;
  // A link that adds Tripmo in one click, where the AI has one.
  oneClick?: { href: string; label: string; note: string };
  ways: Way[];
  // The day each step was last checked against `sources`, the AI's own help pages (YYYY-MM-DD).
  checked: string;
  sources: { title: string; url: string }[];
}

// The step every way ends on, in the app: their AI opens Tripmo's sign-in, then the page that asks them to allow it.
export const SIGN_IN = "Your AI opens Tripmo: sign in as {you} and choose **Allow**.";
// The same step on the public pages, where we don't know who's reading.
export const SIGN_IN_PUBLIC = "Your AI opens Tripmo: sign in, or make an account, and choose **Allow**.";
const signIn: Step = { text: SIGN_IN };

// What to say to your AI to connect it (features/getting-started.md, "Connect your AI"): the same for every AI.
// One that can add connectors itself (OpenCode, Claude Code) does; one that can't (Claude, ChatGPT, Grok on the
// web) walks its person through it.
export const connectPrompt = (url: string) =>
  `I want to connect you to Tripmo, my trip planner, so you can plan my trips in it. Its connector (MCP server) is at ${url}. If you can add it yourself, please do. If not, tell me exactly where to click in this app to add it as a custom connector, step by step.`;

const CHECKED = "2026-10-26";
// The last step of a way the command line or an editor starts: the browser opens Tripmo's pages.
const signInBrowser: Step = { text: "Your browser opens Tripmo: sign in, or make an account, and choose **Allow**." };

// The AIs the app offers by name (Claude, ChatGPT, Grok), and the one for any other; each one's first way is the
// app's steps.
export const CLAUDE: Ai = {
  slug: "claude",
  name: "Claude",
  where: "claude.ai and the Claude apps",
  oneClick: {
    href: `https://claude.ai/customize/connectors?modal=add-custom-connector&connectorName=Tripmo&connectorUrl=${encodeURIComponent(MCP_URL)}`,
    label: "Add Tripmo to Claude",
    note: "Opens Claude's **Add custom connector** with Tripmo's name and address filled in. Check they read Tripmo and app.tripmo.me, choose **Continue**, and sign in to Tripmo.",
  },
  ways: [
    {
      title: "On claude.ai",
      steps: [
        {
          text: "In Claude, open [Customize › Connectors](https://claude.ai/customize/connectors).",
          note: "This needs a computer: claude.ai or the Claude app. Once it's added, Claude on your phone has Tripmo too.",
        },
        {
          text: "Choose **+ Add**, then **Add custom connector**. Name it Tripmo and paste this address:",
          address: true,
        },
        { text: "Leave the sign-in settings as they are. Choose **Add**, then **Connect**." },
        signIn,
      ],
      note: "Claude's free plan adds one custom connector. On a Team or Enterprise plan, an owner adds it first, in Organization settings.",
    },
  ],
  checked: CHECKED,
  sources: [
    { title: "Claude Help Center", url: "https://support.claude.com/en/articles/11175166" },
    { title: "Claude's connector docs", url: "https://claude.com/docs/connectors/custom/remote-mcp" },
  ],
};

export const CHATGPT: Ai = {
  slug: "chatgpt",
  name: "ChatGPT",
  where: "chatgpt.com, on Business, Enterprise and Edu",
  ways: [
    {
      title: "On chatgpt.com",
      steps: [
        {
          text: "In ChatGPT, open [Plugins](https://chatgpt.com/plugins).",
          note: "This needs chatgpt.com on a computer. From a phone, do it there once.",
        },
        {
          text: "Choose **+**, then **Add custom MCP server**. Name it Tripmo and, under Connection, paste this address:",
          address: true,
        },
        {
          text: "Keep authentication on **OAuth**, tick **I understand and want to continue**, choose **Create as a plugin**, then install it.",
        },
        signIn,
      ],
      note: "Adding your own needs ChatGPT Business, Enterprise or Edu; in a workspace, an admin or owner adds it. On Pro, ChatGPT can only read through it, so it can't plan in Tripmo.",
    },
  ],
  checked: CHECKED,
  sources: [
    { title: "OpenAI developers", url: "https://developers.openai.com/plugins/deploy/connect-chatgpt" },
    { title: "OpenAI Help Center", url: "https://help.openai.com/en/articles/12584461" },
  ],
};

export const GROK: Ai = {
  slug: "grok",
  name: "Grok",
  where: "grok.com, Grok Build and Grok Bot",
  ways: [
    {
      title: "On grok.com",
      steps: [
        { text: "In Grok, open [Connectors](https://grok.com/connectors)." },
        { text: "Choose **New Connector**, then **Custom**, and paste this address:", address: true },
        signIn,
      ],
      note: "In Grok Business or Enterprise, an admin adds connectors first.",
    },
    {
      title: "In Grok Build",
      steps: [
        { text: "In a terminal, add Tripmo:", copy: `grok mcp add --transport http tripmo ${MCP_URL}` },
        {
          text: "The first time Grok Build uses Tripmo, your browser opens it: sign in, or make an account, and choose **Allow**.",
        },
      ],
    },
    {
      title: "In Grok Bot",
      steps: [
        {
          text: "In a chat with Grok Bot, ask it to add Tripmo as a custom server:",
          copy: `Add a custom MCP server called tripmo at ${MCP_URL}`,
        },
        { text: "When it asks, tell it to go ahead." },
        signIn,
      ],
      note: "Grok Bot's own help doesn't describe this yet: it's the way people add a server of their own today. Its Connect apps list will have Tripmo once it's listed there.",
    },
  ],
  checked: CHECKED,
  sources: [
    { title: "xAI's Connectors docs", url: "https://docs.x.ai/grok/connectors" },
    { title: "Grok Build's MCP docs", url: "https://docs.x.ai/build/features/mcp-servers" },
    { title: "Grok Bot's apps docs", url: "https://docs.x.ai/grok-bot/computer-and-apps" },
  ],
};

export const GEMINI: Ai = {
  slug: "gemini",
  name: "Gemini",
  where: "gemini.google.com",
  ways: [
    {
      title: "On gemini.google.com",
      steps: [
        {
          text: "On [gemini.google.com](https://gemini.google.com), open **Settings**, then **Connected Apps**.",
          note: "This needs the Gemini web app. Once it's added, Gemini on your phone has Tripmo too.",
        },
        { text: "Under Custom apps, choose **Add a custom app** and paste this address:", address: true },
        { text: "Choose **Next** and follow what it asks." },
        signIn,
      ],
      note: "Custom apps in Gemini are for personal Google accounts in the US, 18 or older, with Keep Activity on, in English.",
    },
  ],
  checked: CHECKED,
  sources: [{ title: "Gemini Apps Help", url: "https://support.google.com/gemini/answer/17209137" }],
};

export const CURSOR: Ai = {
  slug: "cursor",
  name: "Cursor",
  where: "The Cursor editor",
  oneClick: {
    href: `cursor://anysphere.cursor-deeplink/mcp/install?name=tripmo&config=${btoa(JSON.stringify({ url: MCP_URL }))}`,
    label: "Add Tripmo to Cursor",
    note: "Opens Cursor, which asks you to confirm adding Tripmo. Then sign in to Tripmo when it asks. If nothing opens, update Cursor.",
  },
  ways: [
    {
      title: "By hand",
      steps: [
        {
          text: "Open `~/.cursor/mcp.json` (Cursor's **Customize**, then **MCPs**, shows its servers) and add Tripmo:",
          copy: `{ "mcpServers": { "tripmo": { "url": "${MCP_URL}" } } }`,
        },
        {
          text: "Save it. The first time Cursor uses Tripmo, your browser opens it: sign in, or make an account, and choose **Allow**.",
        },
      ],
    },
  ],
  checked: CHECKED,
  sources: [
    { title: "Cursor's install links", url: "https://cursor.com/docs/mcp/install-links" },
    { title: "Cursor's MCP docs", url: "https://cursor.com/docs/mcp" },
  ],
};

export const VSCODE: Ai = {
  slug: "vscode",
  name: "VS Code",
  where: "GitHub Copilot in VS Code",
  oneClick: {
    href: `vscode:mcp/install?${encodeURIComponent(JSON.stringify({ name: "tripmo", type: "http", url: MCP_URL }))}`,
    label: "Add Tripmo to VS Code",
    note: "Opens VS Code, which asks you to install Tripmo for Copilot's chat. Then sign in to Tripmo when it asks.",
  },
  ways: [
    {
      title: "By hand",
      steps: [
        { text: "In VS Code, open the Command Palette and run **MCP: Add Server**." },
        { text: "Choose **HTTP** and paste this address:", address: true },
        { text: "Name it tripmo and choose **Global**, so every project has it." },
        signInBrowser,
      ],
      note: "With Copilot Business or Enterprise, your organization turns on MCP servers in Copilot first.",
    },
  ],
  checked: CHECKED,
  sources: [
    { title: "VS Code's MCP docs", url: "https://code.visualstudio.com/docs/copilot/customization/mcp-servers" },
    { title: "VS Code's install links", url: "https://code.visualstudio.com/api/extension-guides/ai/mcp" },
  ],
};

export const COMMAND_LINE: Ai = {
  slug: "claude-code",
  name: "Claude Code",
  where: "Also Codex, Gemini CLI and OpenCode",
  ways: [
    {
      title: "In Claude Code",
      steps: [
        { text: "In a terminal, add Tripmo:", copy: `claude mcp add --transport http tripmo ${MCP_URL}` },
        { text: "In Claude Code, run `/mcp`, choose tripmo and sign in." },
        signInBrowser,
      ],
      note: "A connector you added on claude.ai shows up in Claude Code too.",
    },
    {
      title: "In Codex",
      steps: [
        { text: "In a terminal, add Tripmo:", copy: `codex mcp add tripmo --url ${MCP_URL}` },
        { text: "Then sign in:", copy: "codex mcp login tripmo" },
        signInBrowser,
      ],
    },
    {
      title: "In Gemini CLI",
      steps: [
        { text: "In a terminal, add Tripmo:", copy: `gemini mcp add --transport http tripmo ${MCP_URL}` },
        { text: "In Gemini CLI, sign in if it hasn't asked yet:", copy: "/mcp auth tripmo" },
        signInBrowser,
      ],
    },
    {
      title: "In OpenCode",
      steps: [
        {
          text: "Add Tripmo to your `opencode.json`:",
          copy: `{ "mcp": { "tripmo": { "type": "remote", "url": "${MCP_URL}" } } }`,
        },
        { text: "Then sign in:", copy: "opencode mcp auth tripmo" },
        signInBrowser,
      ],
    },
  ],
  checked: CHECKED,
  sources: [
    { title: "Claude Code's MCP docs", url: "https://code.claude.com/docs/en/mcp" },
    { title: "Codex's MCP docs", url: "https://developers.openai.com/codex/mcp" },
    {
      title: "Gemini CLI's MCP docs",
      url: "https://github.com/google-gemini/gemini-cli/blob/main/docs/tools/mcp-server.md",
    },
    { title: "OpenCode's MCP docs", url: "https://opencode.ai/docs/mcp-servers" },
  ],
};

export const MISTRAL: Ai = {
  slug: "mistral",
  name: "Mistral",
  where: "Work, at chat.mistral.ai",
  ways: [
    {
      title: "In Work",
      steps: [
        { text: "In Mistral's Work, open **Connectors** from the sidebar." },
        {
          text: "Choose **+ Add Connector**, then the **Custom MCP Connector** tab. Name it tripmo and paste this address:",
          address: true,
        },
        { text: "Choose **Connect**." },
        signIn,
      ],
      note: "Only an admin adds custom connectors: on Free, Pro and Student plans, that's you; in a team, ask your admin.",
    },
  ],
  checked: CHECKED,
  sources: [{ title: "Mistral's connector docs", url: "https://docs.mistral.ai/vibe/work/connectors/mcp-connectors" }],
};

export const PERPLEXITY: Ai = {
  slug: "perplexity",
  name: "Perplexity",
  where: "perplexity.ai, on Pro, Max and Enterprise",
  ways: [
    {
      title: "On perplexity.ai",
      steps: [
        { text: "In Perplexity, open **Plugins** from the sidebar." },
        {
          text: "Choose **Add custom**, then **Connector**, **Add MCP connector** and **Remote**. Name it Tripmo and paste this address:",
          address: true,
        },
        {
          text: "Set authentication to **OAuth** and transport to **Streamable HTTP**, tick the box and choose **Add**. Then choose Tripmo's card to sign in.",
        },
        signIn,
      ],
      note: "Custom connectors need Perplexity Pro, Max or Enterprise. In Enterprise, an admin allows members to add them first.",
    },
  ],
  checked: CHECKED,
  sources: [{ title: "Perplexity Help Center", url: "https://www.perplexity.ai/help-center/en/articles/13915507" }],
};

export const OTHER: Ai = {
  slug: "other",
  name: "your AI",
  where: "anything that adds a remote MCP server",
  ways: [
    {
      title: "In any AI",
      steps: [
        {
          text: "In your AI's settings, find where it adds connectors (some call them apps, plugins, extensions or MCP servers).",
        },
        {
          text: "Add a new connector with this address. If it asks what kind, it's a remote one. There's no key to copy: your AI signs in through Tripmo.",
          address: true,
        },
        signIn,
      ],
      note: "OpenCode, Claude Code, Codex, Cursor, VS Code, Gemini, Mistral and Perplexity can all add one.",
    },
  ],
  checked: CHECKED,
  sources: [],
};

// A step's words, in parts: plain text, **bold**, [a link](https://…), `code`, and {you}.
export type Part =
  | { kind: "text" | "bold" | "code"; text: string }
  | { kind: "link"; text: string; href: string }
  | { kind: "you" };

const MARKS = /\*\*(.+?)\*\*|\[([^\]]+)\]\((https:\/\/[^)\s]+)\)|`([^`]+)`|\{you\}/g;

export function parts(text: string): Part[] {
  const out: Part[] = [];
  let at = 0;
  for (const m of text.matchAll(MARKS)) {
    if (m.index > at) out.push({ kind: "text", text: text.slice(at, m.index) });
    if (m[1] !== undefined) out.push({ kind: "bold", text: m[1] });
    else if (m[2] !== undefined && m[3] !== undefined) out.push({ kind: "link", text: m[2], href: m[3] });
    else if (m[4] !== undefined) out.push({ kind: "code", text: m[4] });
    else out.push({ kind: "you" });
    at = m.index + m[0].length;
  }
  if (at < text.length) out.push({ kind: "text", text: text.slice(at) });
  return out;
}

// Every AI with a page at tripmo.me/connect/<slug>, in the order the page lists them.
export const ALL: Ai[] = [CLAUDE, CHATGPT, GROK, GEMINI, CURSOR, VSCODE, COMMAND_LINE, MISTRAL, PERPLEXITY];
